import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { z } from 'zod';
import { KTSVG } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import TextWithLineBreaks from '../../../../../modules/components/text/TextWithLineBreaks';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { BrandStatus, CategorizeServiceSection, FieldStatus, SiService } from '../../../mappings';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { schema } from './Schema';
import { SiAiAddBrandsRow } from './SiAiAddBrandsRow';

interface Props {
  data: {
    [SiService.BRAND_DETECT_SERVICE]: any;
    [SiService.CATEGORIZE_SERVICE]: any;
  };
  onClose: () => void;
  onUpdate: UsePageDataManagerResult['updateField'];
  onSaveMultiple: UsePageDataManagerResult['saveMultipleServices'];
  handleRefetchImages: () => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const SiAiAddBrands = ({
  data,
  onClose,
  onUpdate,
  onSaveMultiple,
  handleRefetchImages,
  isLoading,
  setIsLoading,
}: Props) => {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const [errorMessage, setErrorMessage] = useState('');

  const { addBrands } = schema(intl);
  type FormDataType = z.infer<typeof addBrands>;

  const defaultValues: FormDataType = {
    url: data?.[SiService.CATEGORIZE_SERVICE]?.query?.value,
    brands: [
      {
        sector: '',
        name: '',
        isNew: false,
        searchType: '',
        screenshot: null,
      },
    ],
  };
  const methods = useForm({
    resolver: zodResolver(addBrands),
    defaultValues,
  });
  const {
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    formState: { errors },
  } = methods;
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control,
    name: 'brands',
  });

  const {
    data: listBrandsData,
    status,
    refetch,
  } = useQuery(['site-intelligence-category-brands-list'], async () => {
    const { result, status } = await api(
      `admin/v1/list_all_brands`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          filters: {},
          sort_order: { name: 'name', sort: 1 }, // sort by brand name
          offset: 0,
          limit: 0,
        }),
      },
      { token: auth?.accessToken, refresh },
      'site-intelligence',
    ).then(async (resp) => {
      const result = await resp?.json();
      return { result: result, status: result?.code ?? resp?.status };
    });
    if (status === 200) return result;
  });
  const brandList = listBrandsData?.results?.data ?? [];

  const handleClose = () => {
    reset();
    setErrorMessage('');
    onClose();
  };

  const handleAddNewBrand = async (name: string) => {
    try {
      const { result, status } = await api(
        `admin/v1/add_new_brand`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            name,
            status: Object.keys(BrandStatus)[0],
          }),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence',
      ).then((resp) => {
        return { result: resp?.json(), status: resp?.status };
      });

      const resolvedResult = await result;
      if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
        return { success: true, message: '' };
      }

      return {
        success: false,
        message: resolvedResult?.message || 'Fail to add new brand.',
      };
    } catch (err) {
      return {
        success: false,
        message: intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }),
      };
    }
  };

  const onSubmit: SubmitHandler<FormDataType> = async (formData) => {
    setIsLoading(true);

    try {
      const newBrandNames = Array.from(
        new Set(
          formData.brands
            .filter((brand) => brand?.isNew)
            .map((brand) => brand?.name?.trim())
            .filter(Boolean)
        )
      );

      if (newBrandNames.length > 0) {
        await Promise.all(
          newBrandNames.map((brandName) => handleAddNewBrand(brandName))
        );
      }

      onUpdate(SiService.CATEGORIZE_SERVICE, (currentDraft) => {
        const categorizeBrandsDetail = currentDraft?.detail?.find(
          (item) => item.section === CategorizeServiceSection.BRAND
        )?.section_detail;
        const existingUrlObj = categorizeBrandsDetail?.find((u) => u.url === formData.url);
        if (existingUrlObj) {
          formData.brands.forEach((brand) => {
            const existingBrandObj = existingUrlObj?.brands?.find(
              (b) => b?.name === brand?.name && b?.sector === brand?.sector
            );
            if (existingBrandObj) {
              existingBrandObj.capture_id = null;
              existingBrandObj.field_status = FieldStatus.ACTIVE;
            } else {
              existingUrlObj.brands.push({
                capture_id: null,
                name: brand?.name,
                sector: brand?.sector,
                field_status: FieldStatus.ACTIVE,
                snippets: [],
              });
            }
          });
        } else {
          categorizeBrandsDetail?.push({
            capture_id: null,
            url: formData.url,
            brands: formData.brands.map((brand) => ({
              capture_id: null,
              name: brand?.name,
              sector: brand?.sector,
              field_status: FieldStatus.ACTIVE,
              snippets: [],
            })),
          });
        }

        // Update brands in result
        formData.brands.forEach((brand, index) => {
          const existingBrandInResult = currentDraft?.result?.brands?.find(
            (b) => b?.brand === brand?.name
          );
          if (existingBrandInResult) {
            existingBrandInResult.field_status = FieldStatus.ACTIVE;
          } else {
            currentDraft?.result?.brands?.push({
              brand: brand?.name,
              field_status: FieldStatus.ACTIVE,
            });
          }
        });
      });

      // Save draft data
      await onSaveMultiple([
        {
          selector: SiService.CATEGORIZE_SERVICE,
          paths: [
            `detail[${data?.[SiService.CATEGORIZE_SERVICE]?.detail?.findIndex(
              (item) => item.section === CategorizeServiceSection.BRAND
            )}]`,
            'result.brands',
          ],
        },
      ]);

      handleRefetchImages();
      handleClose();
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to add brand/s.',
      });
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='d-flex flex-row-fluid flex-center bg-white rounded'>
      <FormProvider {...methods}>
        <form
          className='pt-5 w-100 w-xl-900px px-md-9'
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <div className='d-flex mb-4 align-items-center'>
            <label className='form-label required w-50'>
              URL <small>(must include http:// or https://)</small>
            </label>
            <Controller
              name='url'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <input
                    {...field}
                    disabled={isLoading}
                    type='text'
                    className={clsx(
                      'form-control fw-normal fs-base',
                      fieldState.invalid && 'border border-danger'
                    )}
                    placeholder='https://example.com'
                  />
                );
              }}
            />
          </div>

          <div className='separator my-6'></div>

          <div className='w-100'>
            {fields.map((field, index) => (
              // key={field.id} to ensure proper update on remove row
              <div className='d-block w-100' key={field.id}>
                <SiAiAddBrandsRow
                  defaultValues={defaultValues.brands[0]}
                  index={index}
                  onRemove={remove}
                  isLoading={isLoading}
                  brandListOptions={brandList}
                />
              </div>
            ))}
          </div>
          <div className='mt-6'>
            <i
              className='bi bi-plus-square-fill fs-1 text-success cursor-pointer'
              onClick={() => {
                append(defaultValues.brands[0]);
              }}
            ></i>
          </div>

          {errorMessage && (
            <div className='text-danger my-4 border border-danger rounded p-2'>
              <TextWithLineBreaks text={errorMessage} />
            </div>
          )}

          <div className='d-flex justify-content-end mt-4'>
            <button
              className='btn btn-primary d-flex align-items-center gap-2'
              type='submit'
              disabled={isLoading}
            >
              {intl.formatMessage({ id: 'COMMON.SUBMIT' })}
              {isLoading ? (
                <span className='spinner-border spinner-border-sm align-middle'></span>
              ) : (
                <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 m-0' />
              )}
            </button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export { SiAiAddBrands };
